import {
  Annotation,
  AnnotationParams,
  UpdatedAnnotations,
} from "../interfaces/annotation";
import { primaryColor } from "../../common/utilities/color";
import { GroupBlockEntityType } from "../../configMap";
import {
  flattenGroupedOutput,
  groupAnnotations,
} from "../../annotation/utils/utils";

export const convertAnnotationsToAdd = (
  annotation: AnnotationParams,
  annotations: Array<Annotation>,
  entityList: Array<GroupBlockEntityType>,
  originalIndex: number | undefined,
): UpdatedAnnotations => {
  const isInExistingAnnotation = annotations.some(
    (pa) =>
      (pa.pageTokenIndices?.filter((tid) =>
        annotation.pageTokenIndices?.includes(tid),
      ).length || 0) > 0 &&
      pa.page === annotation.page &&
      pa.entity.id === annotation.entity.id &&
      pa.index === annotation.index,
  );
  if (isInExistingAnnotation) {
    return { updatedAnnotations: annotations, newAnnotation: undefined };
  }

  const entity = entityList.find(
    (entity) => entity.entityType.id === annotation.entity.id,
  );
  if (!entity) {
    return { updatedAnnotations: annotations, newAnnotation: undefined };
  }

  const filteredAnnotations = annotations.filter(
    (a) => a.multipleGroupBlocks === entity.multipleGroupBlocks,
  );

  const newAnnotation: Annotation = {
    id: `temp_${crypto.randomUUID()}`,
    ...annotation,
    entity: {
      id: entity.entityType.id,
      name: annotation.entity.name,
      color: entity.color || primaryColor,
      entityNormalizations: entity.entityType.entityNormalizations,
      entityType: "NER",
      multipleGroupBlocks: entity.multipleGroupBlocks,
    },
    originalIndex: originalIndex,
    isOutput: false,
    multipleGroupBlocks: entity.multipleGroupBlocks,
  };

  const hasMultipleAnnotations =
    [...filteredAnnotations, newAnnotation].filter(
      (selectedAnnotation) =>
        selectedAnnotation?.entity?.id === annotation.entity.id &&
        selectedAnnotation?.index === annotation?.index,
    ).length > 1;

  newAnnotation.triggerNormalization =
    !hasMultipleAnnotations && !!entity.entityType.entityNormalizations?.length;

  // Check and normalize first annotation of this entityAnnotation when it isn't normalized yet
  const prevAnnotationsWithTriggerNormalization = [...filteredAnnotations].map(
    (currentAnnotation) => {
      if (
        hasMultipleAnnotations &&
        currentAnnotation.index === newAnnotation.index &&
        currentAnnotation.entity.id === newAnnotation.entity.id
      ) {
        const triggerNormalization: boolean =
          currentAnnotation.isOutput &&
          !!currentAnnotation.entityAnnotationNormalization &&
          !!entity.entityType.entityNormalizations?.length;

        return {
          ...currentAnnotation,
          values: newAnnotation.values,
          pageTokenIndices: newAnnotation.pageTokenIndices,
          modelScore: 100,
          isByUser: true,
          triggerNormalization: triggerNormalization,
          page: newAnnotation.page,
          entity: newAnnotation.entity,
        };
      }

      return currentAnnotation;
    },
  );

  if (hasMultipleAnnotations) {
    return {
      updatedAnnotations: prevAnnotationsWithTriggerNormalization,
      newAnnotation: newAnnotation,
    };
  }

  return {
    updatedAnnotations: [
      ...prevAnnotationsWithTriggerNormalization,
      newAnnotation,
    ],
    newAnnotation: newAnnotation,
  };
};

export const convertToRemoveAnnotations = (
  removeId: string,
  annotations: Array<Annotation>,
  multipleGroupBlocks: boolean,
) => {
  const filteredAnnotations = annotations.filter(
    (a) => a.multipleGroupBlocks === multipleGroupBlocks,
  );

  const annotationToDelete = filteredAnnotations.find((a) => a.id === removeId);

  if (annotationToDelete?.isOutput) {
    // Find new first annotation of entityAnnotation and normalize this if needed
    const groupedAnnotations = groupAnnotations(
      [...filteredAnnotations].filter((a) => a.id !== removeId),
    );
    const annotationsOutput = flattenGroupedOutput(groupedAnnotations);
    return annotationsOutput.map((annotation) => {
      if (
        annotation.entity.id === annotationToDelete.entity.id &&
        annotation.isOutput &&
        !annotation.entityAnnotationNormalization &&
        annotationToDelete.entity.entityNormalizations?.length
      ) {
        return {
          ...annotation,
          triggerNormalization: true,
        };
      }

      return annotation;
    });
  }

  return [...filteredAnnotations].filter((a) => a.id !== removeId);
};
